import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot } from '@angular/router';
import jwt_decode from 'jwt-decode';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthenticationService } from '../_services/authentication.service';
import { RoleStateService } from '../_services/role-state.service';

/**
 * Set as route `data.permission` to require an exact module (and optionally function) match
 * instead of the legacy substring matching. The deepest route in the URL that declares it wins.
 */
export interface RoutePermission {
  module: string;
  function?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate, CanActivateChild {

  constructor(
    private authService: AuthenticationService,
    private router: Router,
    private roleStateService: RoleStateService
  ) { }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
    return this.authorize(state);
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
    return this.authorize(state);
  }

  private authorize(state: RouterStateSnapshot): Observable<boolean> | boolean {
    const publicRoutes = ['login', 'forgot-password'];
    const urlSegments = state.url.split('?')[0].split('/');

    if (publicRoutes.includes(urlSegments[1])) {
      return true;
    }

    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['login']);
      return false;
    }

    if (!this.roleStateService.isRolesLoaded) {
      const userId = this.resolveUserId();
      if (!userId) {
        this.router.navigate(['login']);
        return false;
      }
      return this.roleStateService.loadRoles(userId).pipe(
        map(() => this.checkAccess(state, urlSegments))
      );
    }

    return this.checkAccess(state, urlSegments);
  }

  private resolveUserId(): string | null {
    const stored = localStorage.getItem('id');
    if (stored) {
      return stored;
    }
    try {
      const token = this.authService.currentUserValue?.id_token;
      const userId = token ? (jwt_decode(token) as any)?.UserId : null;
      if (userId === null || userId === undefined || userId === '') {
        return null;
      }
      localStorage.setItem('id', String(userId));
      return String(userId);
    } catch {
      return null;
    }
  }

  private checkAccess(state: RouterStateSnapshot, urlSegments: string[]): boolean {
    const permission = this.findRoutePermission(state.root);
    const allowed = permission
      ? this.hasStrictPermission(permission, this.roleStateService.roles)
      : this.checkPermissions(this.roleStateService.roles, urlSegments);
    if (!allowed && permission) {
      this.router.navigate(['forbidden']);
    }
    return allowed;
  }

  private findRoutePermission(root: ActivatedRouteSnapshot): RoutePermission | null {
    let found: RoutePermission | null = null;
    let node: ActivatedRouteSnapshot | null = root;
    while (node) {
      const p = node.routeConfig?.data?.['permission'] as RoutePermission | undefined;
      if (p?.module) {
        found = p;
      }
      node = node.firstChild;
    }
    return found;
  }

  private hasStrictPermission(permission: RoutePermission, roles: any[]): boolean {
    const module = this.normalize(permission.module);
    const modules = roles.filter((mod: any) => this.normalize(mod.moduleUrl) === module);
    if (!modules.length) {
      return false;
    }
    if (!permission.function) {
      return true;
    }
    const func = this.normalize(permission.function);
    return modules.some((mod: any) => mod.functions?.some((f: any) => {
      const url = this.normalize(f.functionUrl);
      return url === func || url === `${module}/${func}`;
    }));
  }

  private normalize(value: any): string {
    return String(value ?? '').trim().replace(/^\/+|\/+$/g, '').toLowerCase();
  }

  private checkPermissions(roles: any[], urlSegments: string[]): boolean {
    const currentModule = urlSegments.length > 2 ? urlSegments[2] : null;
    const currentFunction = urlSegments.length > 3 ? urlSegments[3] : null;

    if (currentModule && !this.hasModulePermission(currentModule, roles)) {
      if (currentModule === 'profile' || currentModule === 'helpdesk' || currentModule === 'tax-form' ||
        currentModule === 'activity' || currentModule === 'user-manual' || currentModule === 'deploy') {
        return true;
      }
      this.router.navigate(['forbidden']);
      return false;
    }

    if (currentFunction && !this.hasFunctionPermission(currentModule, currentFunction, roles)) {
      if (currentFunction.includes('edit-employee') || currentFunction.includes('add-employee') ||
        currentFunction.includes('view-profile') || currentFunction.includes('raise-ticket') ||
        currentFunction.includes('mid-year-review') ||
        currentFunction.includes('manager-review')) {
        return true;
      }
      this.router.navigate(['forbidden']);
      return false;
    }

    return true;
  }

  private hasModulePermission(url: string, roles: any[]): boolean {
    return roles.some((mod: any) => url.includes(mod.moduleUrl));
  }

  private hasFunctionPermission(module: string | null, func: string, roles: any[]): boolean {
    if (!module) return false;
    return roles.some((role: any) =>
      role.moduleUrl === module &&
      role.functions?.some((f: any) => f.functionUrl?.includes(func))
    );
  }
}
